import React, { useEffect, useRef, useState } from 'react';
import { authApiClient } from '../../api/authApiClient';
import type { TwoFactorEnrollment } from '../../api/twoFactorApiClient';
import { handleApiError } from '../../../../shared/utils/handleApiError';
import type { LoginResponse } from '../../type';
import { AuthInput } from '../ui/AuthInput';
import Button from '../../../../shared/components/Button';
import { TwoFactorQrPanel, BackupCodesPanel } from '../TwoFactorPanels';

interface TwoFactorEnrollmentFormProps {
  /**
   * Single-use token from the TWO_FACTOR_ENROLLMENT_REQUIRED login response.
   * Bearer-equivalent for 5 minutes — held in the parent's component state only,
   * never persisted and never in the URL.
   */
  pendingToken: string;
  /**
   * Enrolment finished AND the user confirmed they saved their backup codes.
   * Fires once, with the session watchman issued in the confirm call.
   */
  onEnrolled: (response: LoginResponse) => void;
  /** The 5-minute pending session lapsed; the parent returns to the password step. */
  onExpired: () => void;
  /** User chose to start over rather than finish enrolling. */
  onCancel: () => void;
}

type Step = 'loading' | 'scan' | 'codes';

/**
 * Signed-out 2FA enrolment (PE-2157, unblocked by watchman #102 / PE-2229).
 *
 * Reached when 2FA is mandatory for an account that never set it up: the
 * password was right, but watchman issues no session until a secret exists.
 * This is the ONLY path out of that state — there is no signed-in screen to
 * fall back to, which is precisely the deadlock #102 fixed.
 *
 * ⚠️ Ordering matters here in a way it does not on the Security page. The
 * confirm call returns the session AND the ten backup codes together, and the
 * codes are shown exactly once. So `onEnrolled` fires only after the codes are
 * acknowledged — completing the login first would navigate away and destroy the
 * only copy. The session is parked in a ref until then.
 */
export const TwoFactorEnrollmentForm: React.FC<TwoFactorEnrollmentFormProps> = ({
  pendingToken,
  onEnrolled,
  onExpired,
  onCancel,
}) => {
  const [step, setStep] = useState<Step>('loading');
  const [enrollment, setEnrollment] = useState<TwoFactorEnrollment | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [acknowledged, setAcknowledged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  // Parked rather than stated: this is the live session, and re-rendering with
  // it is not the point — it is handed over once, when the codes are confirmed.
  const sessionRef = useRef<LoginResponse | null>(null);

  // watchman's message is the only thing separating "wrong code" (retry in
  // place) from "pending session gone" (start over) — both arrive as a 401.
  const isExpiry = (message: string) => /two-factor session expired/i.test(message);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await authApiClient.startEnrollment(pendingToken);
        if (cancelled) return;
        setEnrollment(next);
        setStep('scan');
      } catch (error: unknown) {
        if (cancelled) return;
        const message = handleApiError(error);
        // Nothing has been shown yet, so an expired token here means the whole
        // attempt is dead — send them back rather than rendering an empty QR.
        if (isExpiry(message)) {
          onExpired();
          return;
        }
        setServerError(message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // pendingToken identifies the attempt; the callbacks are stable for its
    // lifetime, and re-running this would burn a second server-side secret.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingToken]);

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setServerError('Enter the code from your authenticator app.');
      return;
    }

    setIsSubmitting(true);
    setServerError('');
    try {
      const { backupCodes: codes, ...session } = await authApiClient.completeEnrollment(
        pendingToken,
        trimmed,
      );
      sessionRef.current = session;
      setBackupCodes(codes);
      setAcknowledged(false);
      setStep('codes');
    } catch (error: unknown) {
      const message = handleApiError(error);
      if (isExpiry(message)) {
        onExpired();
        return;
      }
      setServerError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === 'codes') {
    return (
      <div className="space-y-8">
        <div className="text-center">
          <h1 className="text-[28px] font-bold text-foreground mb-2">You&apos;re all set</h1>
          <p className="text-sm text-muted-foreground">
            Two-step verification is on for this account.
          </p>
        </div>

        <BackupCodesPanel
          codes={backupCodes}
          acknowledged={acknowledged}
          onAcknowledgedChange={setAcknowledged}
        />

        <Button
          type="button"
          className="w-full"
          disabled={!acknowledged}
          onClick={() => {
            // Guaranteed non-null: this step is only reachable once
            // completeEnrollment has resolved and parked the session.
            if (sessionRef.current) onEnrolled(sessionRef.current);
          }}>
          Continue
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1 className="text-[28px] font-bold text-foreground mb-2">Set up two-step verification</h1>
        <p className="text-sm text-muted-foreground">
          This account requires two-step verification before it can sign in. Scan the code with
          an authenticator app to finish setting it up.
        </p>
      </div>

      {step === 'loading' && !serverError && (
        <p className="text-sm text-muted-foreground text-center">Preparing your setup code…</p>
      )}

      {step === 'scan' && enrollment && (
        <form onSubmit={handleConfirm} className="space-y-6">
          <div className="flex justify-center">
            <TwoFactorQrPanel enrollment={enrollment} />
          </div>

          <AuthInput
            name="code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="123456"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              if (serverError) setServerError('');
            }}
            error={serverError || undefined}
            required
          />

          <Button type="submit" loading={isSubmitting} className="w-full">
            Confirm and sign in
          </Button>
        </form>
      )}

      {/* A failure before the QR renders leaves no form to attach the error to. */}
      {step === 'loading' && serverError && (
        <p className="text-sm text-destructive text-center">{serverError}</p>
      )}

      <button
        type="button"
        onClick={onCancel}
        className="w-full text-sm text-muted-foreground hover:text-foreground hover:underline transition-colors">
        Use a different account
      </button>
    </div>
  );
};
